import { useEffect, useRef, useState } from "react";
import { Upload, Search, Trash2, Copy, Check, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

export default function Media() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [copiedId, setCopiedId] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media", { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [q]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) { const fd = new FormData(); fd.append("file", f); await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      toast.success(`Uploaded ${files.length} file(s)`); load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => { if (!window.confirm("Remove this image?")) return; try { await api.delete(`/admin/media/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const saveMeta = async () => { try { await api.put(`/admin/media/${edit.id}`, { alt: edit.alt, caption: edit.caption }); toast.success("Saved"); setEdit(null); load(); } catch (e) { toast.error(apiErr(e)); } };
  const copy = (m) => { navigator.clipboard.writeText(m.url); setCopiedId(m.id); toast.success("URL copied"); setTimeout(() => setCopiedId(""), 1500); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div><div className="label-mono mb-1">Assets</div><h1 className="font-display font-extrabold text-white text-3xl">Media Library</h1></div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload" className="inline-flex items-center gap-2 bg-brand text-brand-dark font-bold text-sm px-5 py-2.5 rounded-full hover:bg-brand-neon transition-colors disabled:opacity-60"><Upload size={16} /> {uploading ? "Uploading..." : "Upload images"}</button>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} />
      </div>

      <div className="flex items-center gap-2 bg-ink-900 border border-ink-600 rounded-full px-4 py-2 max-w-sm mb-6">
        <Search size={15} className="text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or alt text..." className="bg-transparent outline-none text-sm text-white flex-1 placeholder:text-slate-600" />
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 text-slate-500 border border-dashed border-ink-600 rounded-xl"><ImageIcon size={32} className="mx-auto mb-3 opacity-50" />No media yet. Upload your first image.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="media-grid">
          {items.map((m) => (
            <div key={m.id} className="rounded-xl border border-ink-600 bg-ink-900 overflow-hidden group">
              <div className="aspect-square overflow-hidden relative">
                <img src={m.url} alt={m.alt || m.filename} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => copy(m)} className="w-8 h-8 rounded-full bg-brand text-brand-dark flex items-center justify-center">{copiedId === m.id ? <Check size={14} /> : <Copy size={14} />}</button>
                  <button onClick={() => setEdit(m)} className="w-8 h-8 rounded-full bg-ink-800 text-white flex items-center justify-center text-xs">Aa</button>
                  <button onClick={() => del(m.id)} className="w-8 h-8 rounded-full bg-ink-800 text-red-400 flex items-center justify-center"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="p-2"><div className="text-xs text-slate-300 truncate">{m.filename}</div><div className="text-[10px] text-slate-600">{Math.round((m.size || 0) / 1024)} KB</div></div>
            </div>
          ))}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60" onClick={() => setEdit(null)}>
          <div className="bg-ink-900 border border-ink-600 rounded-xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <img src={edit.url} alt="" className="w-full h-40 object-cover rounded-lg mb-4" />
            <label className="text-xs text-slate-400 font-mono-label">Alt text</label>
            <input value={edit.alt || ""} onChange={(e) => setEdit({ ...edit, alt: e.target.value })} className="w-full bg-ink-800 border border-ink-600 rounded-lg px-3 py-2 text-sm mt-1.5 mb-3 outline-none focus:border-brand text-white" />
            <label className="text-xs text-slate-400 font-mono-label">Caption</label>
            <input value={edit.caption || ""} onChange={(e) => setEdit({ ...edit, caption: e.target.value })} className="w-full bg-ink-800 border border-ink-600 rounded-lg px-3 py-2 text-sm mt-1.5 mb-4 outline-none focus:border-brand text-white" />
            <div className="flex gap-2 justify-end"><button onClick={() => setEdit(null)} className="text-sm text-slate-400 px-4 py-2">Cancel</button><button onClick={saveMeta} className="text-sm font-bold bg-brand text-brand-dark px-4 py-2 rounded-full">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
